<template>
  <section
    v-if="cargando || error || debeMostrar || bancoVacio"
    class="frases-del-dia"
    :class="{ 'frases-del-dia--compact': compact }"
  >
    <div v-if="cargando" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-warning"></div>
      <span v-if="!compact" class="text-muted small ms-2">Cargando inspiración…</span>
    </div>

    <div v-else-if="error" class="alert alert-warning small py-2 mb-0">
      <i class="bi bi-exclamation-triangle me-1"></i>{{ error }}
    </div>

    <div v-else-if="bancoVacio" class="text-muted small">
      No hay frases activas. Añade algunas en la sección Inspiración.
    </div>

    <template v-else-if="debeMostrar">
      <div
        v-if="!compact"
        class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
      >
        <div>
          <h4 class="mb-0">
            <i class="bi bi-stars me-2 text-warning"></i>Inspiración del día
          </h4>
          <small class="text-muted">{{ fechaLegible }}</small>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-warning"
            :disabled="refrescando || !datos.frases.length"
            @click="refrescar"
          >
            <span v-if="refrescando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-shuffle me-1"></i>
            Otras tres frases
          </button>
          <router-link v-if="mostrarEnlace" to="/inspiracion?tab=hoy" class="btn btn-sm btn-outline-warning">
            Gestionar <i class="bi bi-arrow-right ms-1"></i>
          </router-link>
        </div>
      </div>

      <div v-else class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h5 class="mb-0">
          <i class="bi bi-stars me-2 text-warning"></i>Inspiración
        </h5>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-warning"
            :disabled="refrescando || !datos.frases.length"
            title="Otras tres frases"
            @click="refrescar"
          >
            <span v-if="refrescando" class="spinner-border spinner-border-sm"></span>
            <i v-else class="bi bi-shuffle"></i>
          </button>
          <router-link to="/inspiracion?tab=hoy" class="btn btn-sm btn-link p-0">
            Ver todo <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>
      </div>

      <div class="row g-3">
        <div
          v-for="frase in datos.frases"
          :key="frase.id"
          class="col-12"
          :class="compact ? '' : 'col-lg-4'"
        >
          <div
            class="card h-100 shadow-sm frase-card"
            :class="{ 'frase-card--leida': frase.leida }"
          >
            <div class="card-body d-flex flex-column">
              <blockquote class="blockquote mb-2 flex-grow-1">
                <p class="mb-2 frase-texto">"{{ frase.texto }}"</p>
                <footer class="blockquote-footer mb-0">
                  {{ frase.autor || 'Anónimo' }}
                  <cite v-if="frase.fuente" class="ms-1 text-muted">— {{ frase.fuente }}</cite>
                </footer>
              </blockquote>
              <div class="d-flex gap-2 align-items-center mt-auto pt-2 border-top">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="frase.leida ? 'btn-success' : 'btn-outline-secondary'"
                  :disabled="marcando === frase.id"
                  :title="frase.leida ? 'Marcada como leída' : 'Marcar como leída'"
                  @click="marcarLeida(frase)"
                >
                  <span v-if="marcando === frase.id" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi" :class="frase.leida ? 'bi-check-circle-fill' : 'bi-check-circle'"></i>
                  <span class="ms-1">{{ frase.leida ? 'Leída' : 'Marcar como leída' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from '../config/axios';
import { debeMostrarFrasesDelDia } from '../utils/frasesDelDia';

export default {
  name: 'FrasesDelDia',
  props: {
    compact: { type: Boolean, default: false },
    mostrarEnlace: { type: Boolean, default: true },
  },
  setup() {
    const datos = ref(null);
    const cargando = ref(false);
    const refrescando = ref(false);
    const error = ref('');
    const marcando = ref(null);

    const fechaLegible = computed(() => {
      if (!datos.value?.dia) return '';
      const [y, m, d] = datos.value.dia.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    });

    const debeMostrar = computed(() => debeMostrarFrasesDelDia(datos.value));
    const todasLeidas = computed(() => !!datos.value?.todasLeidas);
    const bancoVacio = computed(() => (
      !!datos.value && !datos.value.todasLeidas && (!datos.value.frases || !datos.value.frases.length)
    ));

    async function cargar() {
      cargando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/frases/hoy');
        datos.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudieron cargar las frases';
      } finally {
        cargando.value = false;
      }
    }

    async function refrescar() {
      refrescando.value = true;
      error.value = '';
      try {
        const { data } = await axios.post('/api/frases/hoy/refrescar');
        datos.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudieron cargar otras frases';
      } finally {
        refrescando.value = false;
      }
    }

    async function marcarLeida(frase) {
      const leida = !frase.leida;
      marcando.value = frase.id;
      try {
        const { data } = await axios.post('/api/frases/hoy/leida', {
          frase_id: frase.id,
          leida,
        });
        datos.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al marcar como leída';
      } finally {
        marcando.value = null;
      }
    }

    onMounted(cargar);

    return {
      datos,
      cargando,
      refrescando,
      error,
      marcando,
      fechaLegible,
      debeMostrar,
      bancoVacio,
      todasLeidas,
      marcarLeida,
      cargar,
      refrescar,
    };
  },
};
</script>

<style scoped>
.frase-card {
  border-left: 4px solid #ffc107;
}

.frase-card--leida {
  opacity: 0.72;
  border-left-color: #198754;
}

.frase-texto {
  font-size: 1rem;
  line-height: 1.5;
  color: #343a40;
}

.frases-del-dia--compact .frase-texto {
  font-size: 0.95rem;
}

.frases-del-dia--compact .frase-card {
  margin-bottom: 0;
}
</style>
